/**
 * RightPanelContainer — Switches between Job List and Resume Canvas views.
 *
 * The panel transitions from LIST mode (showing job search results) to
 * CANVAS mode (showing the streaming resume optimizer) when the agent
 * identifies a job selection for optimization.
 */

import { Briefcase, Check, Copy, Download, FileText, Pencil, Search, Sparkles, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useCanvas } from './useCanvas';
import StreamingCanvas from './StreamingCanvas';
import type { AgentJob } from '../components/AgentJobsPanel';
import type { AppliedJobInfo } from '../hooks/useAppliedJobsMap';
import type { RecommendationResult } from '../../recommendations/types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LetterArtifact {
  mode: 'cover' | 'application';
  title: string;
  subtitle: string;
  body: string;
}

/** Describes the current agent processing activity for the right-panel progress bar. */
export interface AgentActivity {
  kind: 'idle' | 'job-search' | 'resume-optimize' | 'cover-letter' | 'application-letter' | 'web-search' | 'feed-refresh';
  /** Sub-phase within the activity for fine-grained progress. */
  phase?: string;
  /** Optional context label (e.g. job title being optimized for). */
  label?: string;
}

interface RightPanelContainerProps {
  // Job list props (passed through when in LIST mode)
  jobListView: React.ReactNode;
  showJobList?: boolean;
  jobs?: AgentJob[];
  recommendations?: Map<string, RecommendationResult>;
  jobsLoading?: boolean;
  onCloseJobs?: () => void;
  onAgentOptimize?: (job: AgentJob) => void;
  onJobOpen?: (jobId: string | null) => void;
  quickStartView?: React.ReactNode;
  letterArtifact?: LetterArtifact | null;
  isGeneratingLetter?: boolean;
  onClearLetter?: () => void;

  /** Current agent processing activity — drives the right-panel progress overlay. */
  agentActivity?: AgentActivity;

  // Canvas action handlers
  onApply?: () => void;
  onDownload?: (format: 'pdf' | 'docx') => void;
  onEmail?: () => void;
  onSaveToProfile?: () => void;
  onCoverLetter?: () => void;
  onApplicationNote?: () => void;
  onStartOptimization?: () => void;
  onOpenConversation?: () => void;
  /** Optimized / applied status for the active canvas job. */
  jobAppliedInfo?: AppliedJobInfo | null;

  // Engagement loop callbacks
  onFindSimilarJobs?: () => void;
  onInterviewPrep?: () => void;
  onTrackApplications?: () => void;

  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RightPanelContainer({
  jobListView,
  showJobList = true,
  jobs = [],
  recommendations = new Map(),
  jobsLoading = false,
  onCloseJobs,
  onAgentOptimize,
  onJobOpen,
  quickStartView,
  letterArtifact,
  isGeneratingLetter = false,
  onClearLetter,
  agentActivity,
  onApply,
  onDownload,
  onEmail,
  onSaveToProfile,
  onCoverLetter,
  onApplicationNote,
  onStartOptimization,
  onOpenConversation,
  jobAppliedInfo,
  onFindSimilarJobs,
  onInterviewPrep,
  onTrackApplications,
  className = '',
}: RightPanelContainerProps) {
  const { state, backToList, setMode } = useCanvas();

  const hasCanvasContent = (state.sections ?? []).length > 0 || !!state.streamingSection;
  const isActive = agentActivity && agentActivity.kind !== 'idle';

  // When showing job results, render AgentJobsPanel directly (it has its own toolbar/header)
  if (state.mode === 'LIST' && showJobList) {
    return (
      <div className={`relative flex flex-col h-full overflow-hidden ${className}`}
        style={{ background: 'var(--ds-bg-page)' }}>

        {/* Agent activity progress overlay — covers entire right panel during processing */}
        {isActive && (jobs?.length ?? 0) === 0 && (
          <RightPanelProgress activity={agentActivity!} />
        )}

        {/* Canvas restore banner — shown when there is resume content to return to */}
        {hasCanvasContent && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 14px',
              background: 'var(--ds-brand-soft, rgba(99,102,241,0.07))',
              borderBottom: '1px solid var(--ds-brand-border, rgba(99,102,241,0.18))',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              {/* Document icon */}
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <rect x="2" y="1" width="9" height="13" rx="1.5" stroke="var(--ds-brand,#6366f1)" strokeWidth="1.3"/>
                <path d="M5 5h5M5 8h5M5 11h3" stroke="var(--ds-brand,#6366f1)" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-brand,#6366f1)' }}>
                Resume ready
              </span>
              {state.job && (
                <span style={{ fontSize: 11, color: 'var(--ds-text-secondary)' }}>
                  · {state.job.title} @ {state.job.company}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setMode('CANVAS')}
              style={{
                fontSize: 11, fontWeight: 600,
                color: 'var(--ds-brand,#6366f1)',
                background: 'var(--ds-bg-card)',
                border: '1px solid var(--ds-brand-border, rgba(99,102,241,0.30))',
                borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <rect x="0.5" y="0.5" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M3 5.5h5M3 3.5h5M3 7.5h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
              </svg>
              View Canvas
            </button>
          </div>
        )}

        {jobListView}
      </div>
    );
  }

  return (
    <div className={`relative flex flex-col h-full overflow-hidden ${className}`} style={{ background: 'var(--ds-bg-page)' }}>

      {/* Legacy letter artifact card — only shown outside canvas mode.
          When canvas is active, the letter lives in the Cover Letter tab of StreamingCanvas. */}
      {(letterArtifact || isGeneratingLetter) && state.mode !== 'CANVAS' && (
        <div className="shrink-0 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <DocumentArtifactCard
            artifact={letterArtifact}
            isLoading={isGeneratingLetter && !letterArtifact}
            onClear={onClearLetter}
          />
        </div>
      )}

      {state.mode === 'CANVAS' ? (
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {/* Agent activity progress overlay — shown on top of canvas during optimization */}
          {isActive && (agentActivity!.kind === 'resume-optimize' || agentActivity!.kind === 'cover-letter' || agentActivity!.kind === 'application-letter') && (
            <RightPanelProgress activity={agentActivity!} overlay />
          )}
          <StreamingCanvas
            onApply={onApply}
            onDownload={onDownload}
            onEmail={onEmail}
            onSaveToProfile={onSaveToProfile}
            onBack={backToList}
            onCoverLetter={onCoverLetter}
            onApplicationNote={onApplicationNote}
            onStartOptimization={onStartOptimization}
            onOpenConversation={onOpenConversation}
            onFindSimilarJobs={onFindSimilarJobs}
            onInterviewPrep={onInterviewPrep}
            onTrackApplications={onTrackApplications}
            jobAppliedInfo={jobAppliedInfo}
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-8 p-6">
          {state.mode !== 'CANVAS' && !showJobList && !letterArtifact && !isGeneratingLetter && (
            <WorkspaceEmptyState quickStartView={quickStartView} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity config ────────────────────────────────────────────────────────────

const ACTIVITY_CONFIG: Record<
  Exclude<AgentActivity['kind'], 'idle'>,
  { icon: React.ReactNode; title: string; steps: { key: string; label: string }[] }
> = {
  'job-search': {
    icon: <Briefcase className="w-7 h-7" />,
    title: 'Fetching live jobs from channels',
    steps: [
      { key: 'linkedin', label: 'LinkedIn' },
      { key: 'naukri', label: 'Naukri' },
      { key: 'peerlist', label: 'GoGrabJob' },
    ],
  },
  'resume-optimize': {
    icon: <Sparkles className="w-7 h-7" />,
    title: 'Optimizing your resume',
    steps: [
      { key: 'jd', label: 'Parsing JD' },
      { key: 'keywords', label: 'ATS keywords' },
      { key: 'sections', label: 'Rewriting' },
      { key: 'scoring', label: 'Scoring' },
    ],
  },
  'cover-letter': {
    icon: <FileText className="w-7 h-7" />,
    title: 'Generating cover letter',
    steps: [
      { key: 'analyze', label: 'Analyzing role' },
      { key: 'draft', label: 'Drafting' },
      { key: 'polish', label: 'Polishing' },
    ],
  },
  'application-letter': {
    icon: <FileText className="w-7 h-7" />,
    title: 'Generating application letter',
    steps: [
      { key: 'analyze', label: 'Analyzing role' },
      { key: 'draft', label: 'Drafting' },
      { key: 'polish', label: 'Polishing' },
    ],
  },
  'web-search': {
    icon: <Search className="w-7 h-7" />,
    title: 'Searching the web',
    steps: [
      { key: 'query', label: 'Building query' },
      { key: 'fetch', label: 'Fetching results' },
      { key: 'summarize', label: 'Summarizing' },
    ],
  },
  'feed-refresh': {
    icon: <Briefcase className="w-7 h-7" />,
    title: 'Refreshing job feeds',
    steps: [
      { key: 'linkedin', label: 'LinkedIn' },
      { key: 'naukri', label: 'Naukri' },
      { key: 'peerlist', label: 'GoGrabJob' },
    ],
  },
};

function RightPanelProgress({ activity, overlay = false }: { activity: AgentActivity; overlay?: boolean }) {
  const [activeStep, setActiveStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const config = ACTIVITY_CONFIG[activity.kind === 'idle' ? 'job-search' : activity.kind];

  useEffect(() => {
    setActiveStep(0);
    setElapsed(0);
  }, [activity.kind]);

  useEffect(() => {
    if (activity.kind === 'idle') return;
    const interval = window.setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => window.clearInterval(interval);
  }, [activity.kind]);

  useEffect(() => {
    if (activity.kind === 'idle') return;
    const timer = window.setInterval(() => {
      setActiveStep((s) => (s < config.steps.length - 1 ? s + 1 : s));
    }, 2200);
    return () => window.clearInterval(timer);
  }, [activity.kind, config.steps.length]);

  const totalSteps = config.steps.length;
  const progressPct = Math.min(95, 8 + (activeStep / Math.max(totalSteps - 1, 1)) * 55 + elapsed * 2.5);

  const statusText = activity.label
    ? `${config.steps[activeStep]?.label ?? 'Processing'}… · ${activity.label}`
    : `${config.steps[activeStep]?.label ?? 'Processing'}…`;

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center"
      style={{
        background: overlay
          ? 'color-mix(in srgb, var(--ds-bg-page) 82%, transparent)'
          : 'var(--ds-bg-page)',
        backdropFilter: overlay ? 'blur(4px)' : undefined,
      }}
    >
      <div className="ds-channel-fetch-progress">
        <div className="ds-channel-fetch-progress__icon">
          {React.cloneElement(config.icon as React.ReactElement, {
            style: { color: 'var(--ds-brand)' },
          })}
        </div>

        <p className="ds-channel-fetch-progress__title">{config.title}</p>

        <div className="ds-channel-fetch-progress__bar-track">
          <div
            className="ds-channel-fetch-progress__bar-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="ds-channel-fetch-progress__channels">
          {config.steps.map((step, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
              <div
                key={step.key}
                className={[
                  'ds-channel-fetch-progress__step',
                  done ? 'ds-channel-fetch-progress__step--done' : '',
                  active ? 'ds-channel-fetch-progress__step--active' : '',
                ].join(' ')}
              >
                <span className="ds-channel-fetch-progress__step-icon">
                  {done ? <Check className="w-3.5 h-3.5" /> : null}
                </span>
                <span className="ds-channel-fetch-progress__step-label">{step.label}</span>
              </div>
            );
          })}
        </div>

        <p className="ds-channel-fetch-progress__status t-meta">
          {statusText}
          <span className="t-num" style={{ marginLeft: 6 }}>{elapsed}s</span>
        </p>
      </div>
    </div>
  );
}

function WorkspaceEmptyState({ quickStartView }: { quickStartView?: React.ReactNode }) {
  if (!quickStartView) return null;

  return (
    <section className="ds-section">
      <div className="ds-section-header">
        <h3 className="t-label">Quick Start</h3>
      </div>
      {quickStartView}
    </section>
  );
}

function formatSalary(job: AgentJob): string {
  if (job.compensation_text?.trim()) return job.compensation_text.trim();
  if (job.salary_min == null && job.salary_max == null) return 'Not listed';
  const currency = job.salary_currency || '$';
  const fmt = (value: number) =>
    value >= 1000 ? `${currency}${Math.round(value / 1000)}k` : `${currency}${value}`;
  if (job.salary_min != null && job.salary_max != null) return `${fmt(job.salary_min)} - ${fmt(job.salary_max)}`;
  if (job.salary_min != null) return `From ${fmt(job.salary_min)}`;
  return `Up to ${fmt(job.salary_max as number)}`;
}

function JobListingsGrid({
  jobs,
  recommendations,
  isLoading,
  onClose,
  onAgentOptimize,
  onJobOpen,
}: {
  jobs: AgentJob[];
  recommendations: Map<string, RecommendationResult>;
  isLoading: boolean;
  onClose?: () => void;
  onAgentOptimize?: (job: AgentJob) => void;
  onJobOpen?: (jobId: string | null) => void;
}) {
  const visibleJobs = jobs.slice(0, 12);

  return (
    <section className="flex flex-shrink-0 flex-col overflow-hidden">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Top Matched Jobs</h3>
        <div className="flex items-center gap-2">
          {isLoading && (
            <span className="inline-flex items-center gap-2 text-xs font-medium text-purple-600">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-200 border-t-purple-600" />
              Searching
            </span>
          )}
          {onClose && (
            <button type="button" onClick={onClose} className="text-xs font-medium text-slate-400 hover:text-slate-600">
              Clear
            </button>
          )}
        </div>
      </div>

      <div style={{ background: 'var(--ds-bg-card)', borderRadius: 8, border: '1px solid var(--ds-border)' }}>
        {visibleJobs.map((job) => {
          const rec = recommendations.get(job.id);
          const score = typeof rec?.score === 'number' ? Math.round(rec.score) : null;
          const postedDays = job.posted_at
            ? Math.max(1, Math.floor((Date.now() - new Date(job.posted_at).getTime()) / 86_400_000))
            : null;

          return (
            <div
              key={job.id}
              className="group flex cursor-pointer items-center justify-between border-b px-4 py-3 transition-colors last:border-b-0"
              style={{ borderColor: 'var(--ds-border)' }}
              onClick={() => onJobOpen?.(job.id)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--ds-bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
            >
              <div className="min-w-0 flex-1 pr-4">
                <h4 className="truncate text-sm font-semibold" style={{ color: 'var(--ds-text-strong)' }}>{job.title}</h4>
                <p className="text-xs" style={{ color: 'var(--ds-text-muted)' }}>
                  {job.company || 'Company'} •{' '}
                  <span className="font-mono">
                    {job.location || (job.remote ? 'Remote' : 'Location TBA')}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-6 sm:gap-8">
                {score != null && (
                  <span className="font-mono text-sm font-bold tabular-nums" style={{ color: 'var(--ds-brand)' }}>{score}% Match</span>
                )}
                <span className="hidden text-xs tabular-nums sm:inline" style={{ color: 'var(--ds-text-muted)' }}>
                  {formatSalary(job)}
                </span>
                {postedDays != null && (
                  <span className="text-xs tabular-nums" style={{ color: 'var(--ds-text-muted)' }}>{postedDays}d ago</span>
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAgentOptimize?.(job);
                  }}
                  className="rounded px-2 py-0.5 text-[11px] opacity-0 transition-all group-hover:opacity-100 hover:bg-indigo-600 hover:text-white"
                  style={{ border: '1px solid var(--ds-border)', color: 'var(--ds-text-muted)', background: 'transparent' }}
                >
                  Optimize
                </button>
              </div>
            </div>
          );
        })}
        {!isLoading && visibleJobs.length === 0 && (
          <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--ds-text-muted)' }}>
            No matching jobs yet. Run a search from the command dock.
          </div>
        )}
      </div>
    </section>
  );
}

function DocumentArtifactCard({
  artifact,
  isLoading,
  onClear,
}: {
  artifact?: LetterArtifact | null;
  isLoading: boolean;
  onClear?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(artifact?.body ?? '');

  useEffect(() => {
    setDraft(artifact?.body ?? '');
    setEditing(false);
  }, [artifact?.body]);

  const handleCopy = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard is optional */
    }
  };

  const handleExport = () => {
    if (!artifact || !draft) return;
    const blob = new Blob([draft], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title.toLowerCase().replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="flex flex-col flex-shrink-0 overflow-hidden rounded-2xl shadow-sm"
      style={{ border: '1px solid var(--ds-border)', background: 'var(--ds-bg-card)' }}
    >
      <div
        className="flex items-center justify-between gap-3 border-b px-4 py-3 text-sm font-semibold"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)', color: 'var(--ds-text-strong)' }}
      >
        <span>[C] Custom Optimized {artifact?.mode === 'application' ? 'Application Letter' : 'Cover Letter'}</span>
        {artifact && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setEditing((v) => !v)} className="ds-btn ds-btn-ghost ds-btn-sm w-8 h-8 p-0" title="Edit">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={handleCopy} className="ds-btn ds-btn-ghost ds-btn-sm w-8 h-8 p-0" title={copied ? 'Copied' : 'Copy'}>
              <Copy className="h-4 w-4" />
            </button>
            <button type="button" onClick={handleExport} className="ds-btn ds-btn-ghost ds-btn-sm w-8 h-8 p-0" title="Export">
              <Download className="h-4 w-4" />
            </button>
            <button type="button" onClick={onClear} className="ds-btn ds-btn-ghost ds-btn-sm w-8 h-8 p-0 text-red-500 hover:text-red-600" title="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="p-8 flex items-center gap-3 text-sm" style={{ color: 'var(--ds-text-body)' }}>
          <span className="h-4 w-4 rounded-full border-2 border-indigo-200 border-t-indigo-500 animate-spin" />
          Generating document...
        </div>
      ) : artifact ? (
        <div className="p-8">
          <div className="mb-5">
            <p className="text-xl font-semibold" style={{ color: 'var(--ds-text-strong)' }}>{artifact.title}</p>
            <p className="text-sm" style={{ color: 'var(--ds-text-muted)' }}>{artifact.subtitle}</p>
          </div>
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-[520px] w-full resize-y rounded-xl p-6 text-sm leading-7 outline-none"
              style={{
                border: '1px solid var(--ds-brand)',
                background: 'var(--ds-bg-page)',
                color: 'var(--ds-text-body)',
              }}
            />
          ) : (
            <div
              className="min-h-[520px] rounded-xl px-8 py-7 shadow-sm"
              style={{ border: '1px solid var(--ds-border)', background: 'var(--ds-bg-recessed)' }}
            >
              <div className="whitespace-pre-wrap text-sm leading-7" style={{ color: 'var(--ds-text-body)' }}>
                {draft}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default RightPanelContainer;
